import React, { useState } from 'react';
import { GridRenderCellParams } from '@mui/x-data-grid';
import { Icon, IconButton, ListItemIcon, Menu, MenuItem, Tooltip } from '@mui/material';

import { Organisatie } from '../../models/organisatie/organisatie';
import DialogDelete from '../controls/dialogs/DialogDelete';
import DialogEditDisplayName from '../controls/dialogs/DialogEditDisplayName';
import ProgressBar from '../controls/ProgressBar';
import ErrorMessage from '../controls/ErrorMessage';
import { organisatieService } from '../../services/organisaties.service';
import { authService } from '../../services/auth.service';

const OrganisatiesActionMenu = ({ 
  params,
  isZonderGebied,
  onOrganisatiesUpdate
}: { 
  params: GridRenderCellParams<Organisatie>;
  isZonderGebied: boolean;
  onOrganisatiesUpdate: () => void;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [canCrud, setCanCrud] = useState<boolean>(false);

  const [open, setOpenDialogs] = useState({
    delete: false,
    editDisplayName: false,
  });

  React.useEffect(() => {
    setCanCrud(authService.canCrud());
  }, []);

  // Handlers to open/close each dialog
  const openDialog = (dialog: keyof typeof open) => {
    // setSelectedId(id);
    setOpenDialogs((prev) => ({ ...prev, [dialog]: true }));
    handleCloseMenu();
  };

  const closeDialog = (dialog: keyof typeof open) => {
    setOpenDialogs((prev) => ({ ...prev, [dialog]: false }));
    setSelectedId(null);
  };
  
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, id: string) => {
    setSelectedId(id) // id: string
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleConfirmDelete = () => {
    if (selectedId) {
      handleDelete(selectedId);
    }
    closeDialog('delete');
  };

  // event
  const handleDelete = async (organisatieId: string) => {
    if (!organisatieId) {
      console.error('Organisatie id is null');
      return;
    }

    try {
      setLoading(true);
      const response = await organisatieService.deleteOrganisatie(organisatieId, isZonderGebied);

      if (response.ok) {
        setTimeout(() => {
          onOrganisatiesUpdate();
          setLoading(false);
        }, 2000);
      }
    }
    catch (error) {
      setLoading(false);
      console.error('Error deleting organisatie:', error);
      setError('Er is een fout opgetreden');
    }
  }

  // Wijzig naam organisatie

  const handleSubmitDisplayName = (organisatie: Organisatie) => {
    if (selectedId === organisatie.id) {
      handleDisplayName(organisatie);
    }
    closeDialog('editDisplayName');
  };
  
  const handleDisplayName = async (organisatie: Organisatie) => {
    if (!organisatie) {
      console.error('Organisatie is null');
      return;
    }

    try {
      setLoading(true);

      //console.log('Organisatie displayname:', organisatie.displayName);
      const response = await organisatieService.updateOrganisatieDisplayName(organisatie, isZonderGebied);

      if (!response) {
        setLoading(false);
        // Handle error based on HTTP status code
        const errorMessage = await response.text();
        console.error('Failed to update organisatie:', errorMessage);
        throw new Error(errorMessage);
      }
    
      //console.log('Organisatie updated successfully:', response);
      setLoading(false);
      onOrganisatiesUpdate();
    }
    catch (error) {
      setLoading(false);
      console.error('Error updating displayname:', error);
      setError('Er is een fout opgetreden');
    }
  }

  return (
    <>
      {canCrud && (
        <IconButton onClick={(e) => handleOpenMenu(e, params.id as string)}>
          <Tooltip title="Acties" placement="right" arrow>
            <Icon>more_vert</Icon>
          </Tooltip>
        </IconButton>
      )}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => openDialog('delete')}>
          <ListItemIcon>
            <Icon>delete</Icon>
          </ListItemIcon>
          Verwijderen
        </MenuItem>
        <MenuItem onClick={() => openDialog('editDisplayName')}>
          <ListItemIcon>
            <Icon>edit</Icon>
          </ListItemIcon>
          Naam wijzigen
        </MenuItem>
      </Menu>

      <DialogDelete
        open={open.delete}
        name={params.row.displayName || undefined}
        onClose={() => closeDialog('delete')}
        onDelete={handleConfirmDelete}
       />

      <DialogEditDisplayName
        open={open.editDisplayName}
        isZonderGebied={isZonderGebied}
        organisatie={params.row}
        onClose={() => closeDialog('editDisplayName')}
        onSubmit={handleSubmitDisplayName}
       />

      { loading && (
        <ProgressBar />
      )}

      { error && (
        <ErrorMessage message={error} />
      )}
    </>
  );
};

export default OrganisatiesActionMenu;
